import type { TFunction } from 'i18next';

import { company } from '~/company/Company';
import { CompanyName } from '~/company/interface';
import type { PageHeaderConfig } from '~/components/isolated/PageHeader/interfaces';

/**
 * Get PageHeader configuration based on current pathname
 * @param pathname - Current page pathname
 * @param isMobile - Whether the device is mobile
 * @param t - Translation function
 * @returns PageHeader configuration or null if not applicable
 */
export const getPageHeaderConfig = (
  pathname: string,
  isMobile: boolean,
  t: TFunction,
): PageHeaderConfig | null => {
  // Order payment processing page — must stay clean of site navigation
  // for every tenant: EmptyHeader on mobile, HeaderMini flow on desktop
  if (pathname.startsWith('/order-payment/')) {
    return {
      title: '',
      showBackButton: false,
      showSupportButton: false,
      hideOnMobile: true,
      hideOnDesktop: true,
    };
  }

  // DubaiExpressCom always uses the full site header instead of PageHeader
  if (company.isIncludedIn([CompanyName.DubaiExpressCom])) {
    return null;
  }

  // Cart page
  if (pathname === '/cart') {
    return {
      title: t('myCart'),
      showBackButton: true,
      backButtonText: t('continueShopping'),
      backButtonPath: '/',
      showSupportButton: true,
    };
  }

  // Checkout page
  if (pathname === '/checkout') {
    return {
      title: t('checkout'),
      showBackButton: true,
      backButtonText: t('backToCart'),
      backButtonPath: '/cart',
      showSupportButton: true,
    };
  }

  // Cabinet pages - Desktop always shows "Profile", Mobile shows specific page names
  if (pathname.startsWith('/cabinet')) {
    // Main cabinet page
    if (pathname === '/cabinet') {
      return {
        title: t('profile'),
        showBackButton: true,
        showSupportButton: true,
        backButtonText: t('continueShopping'),
        backButtonPath: '/',
      };
    }

    // Personal data page
    if (pathname === '/cabinet/personal-data') {
      return {
        title: isMobile ? t('personalData') : t('profile'),
        showBackButton: true,
        showSupportButton: true,
        backButtonText: t('continueShopping'),
        backButtonPath: '/',
      };
    }

    // Orders page (including tabs: /cabinet/orders/active, /cabinet/orders/history)
    if (pathname.startsWith('/cabinet/orders')) {
      return {
        title: isMobile ? t('orders') : t('profile'),
        showBackButton: true,
        showSupportButton: true,
        backButtonText: t('continueShopping'),
        backButtonPath: '/',
      };
    }

    // Address page
    if (pathname === '/cabinet/address') {
      return {
        title: isMobile ? t('deliveryAddresses') : t('profile'),
        showBackButton: true,
        showSupportButton: true,
        backButtonText: t('continueShopping'),
        backButtonPath: '/',
      };
    }

    // Payment method page
    if (pathname === '/cabinet/payment-method') {
      return {
        title: isMobile ? t('paymentMethods') : t('profile'),
        showBackButton: true,
        showSupportButton: true,
        backButtonText: t('continueShopping'),
        backButtonPath: '/',
      };
    }

    // Referral program pages
    if (
      pathname === '/cabinet/referral' ||
      pathname === '/cabinet/referral/rewards' ||
      pathname === '/cabinet/referral/rewards/'
    ) {
      return {
        title: isMobile ? t('inviteFriend') : t('profile'),
        showBackButton: true,
        showSupportButton: true,
        backButtonText: t('continueShopping'),
        backButtonPath: '/',
      };
    }

    // Promocodes pages
    if (pathname.startsWith('/cabinet/promocodes')) {
      return {
        title: isMobile ? t('promocodes') : t('profile'),
        showBackButton: true,
        showSupportButton: true,
        backButtonText: t('continueShopping'),
        backButtonPath: '/',
      };
    }
  }

  // Legals page
  if (pathname.startsWith('/legals')) {
    return {
      title: isMobile ? t('legals') : t('profile'),
      showBackButton: true,
      showSupportButton: false,
      backButtonText: t('continueShopping'),
      backButtonPath: '/',
    };
  }

  // Contact us page
  if (pathname === '/contact-us') {
    return {
      title: isMobile ? t('contactUs') : t('profile'),
      showBackButton: true,
      showSupportButton: false,
      backButtonText: t('continueShopping'),
      backButtonPath: '/',
    };
  }

  // Our story page
  if (pathname === '/our-story') {
    return {
      title: isMobile ? t('ourStory') : t('profile'),
      showBackButton: true,
      showSupportButton: false,
      backButtonText: t('continueShopping'),
      backButtonPath: '/',
    };
  }

  // Favorites page
  if (pathname === '/favs') {
    return {
      title: isMobile ? t('savedAndPurchased') : t('profile'),
      showBackButton: true,
      showSupportButton: false,
      backButtonText: t('continueShopping'),
      backButtonPath: '/',
    };
  }

  // Order page (with dynamic UID) — V3 renders its own PageHeader from OrderV3
  // because the title needs access to async-loaded order data (number, date, status).
  // For desktop, DesktopLayout returns null for this path so OrderV3's PageHeader
  // is the only header on the page.
  if (pathname.startsWith('/order/')) {
    return {
      title: t('profile'),
      showBackButton: true,
      showSupportButton: false,
      hideOnMobile: true,
      backButtonText: t('continueShopping'),
      backButtonPath: '/',
    };
  }

  if (pathname.startsWith('/c/')) {
    return {
      title: t('catalog'),
      showBackButton: false,
      showSupportButton: false,
      hideOnMobile: false,
      hideOnDesktop: true,
      useCategoryName: true,
    };
  }

  if (pathname.startsWith('/shop')) {
    return {
      title: t('allCategories'),
      showSupportButton: false,
      showBackButton: false,
      hideOnMobile: false,
      hideOnDesktop: true,
    };
  }

  // No header configuration for other pages
  return null;
};
